// PlaceOrder.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/api";
import { toast } from "react-toastify";

export const PlaceOrder = () => {
  const navigate = useNavigate();

  const [cart, setCart] = useState({ items: [] });
  const [paymentMethod, setPaymentMethod] = useState("Cash on delivery"); // default as requested
  const [addr, setAddr] = useState({ street: "", city: "", state: "", zip: "", country: "" });
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [buyerPhone, setBuyerPhone] = useState("");

  // load cart
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const c = await api.cart.view();
        setCart(c || { items: [] });
      } catch (e) {
        console.warn("Failed to load cart:", e?.message || e);
        setCart({ items: [] });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const items = Array.isArray(cart?.items) ? cart.items : [];

  const total = useMemo(
    () => items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.pricePerUnit || 0), 0),
    [items]
  );

  const changeQty = async (productId, qty) => {
    try {
      const updated = await api.cart.update(productId, Math.max(0, Number(qty || 0)));
      setCart(updated || { items: [] });
    } catch (e) {
      toast.warn(e?.data?.error || e?.data?.message || "Failed to update cart");
    }
  };

  const place = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.warn("Your cart is empty.");
    if (!addr.street || !addr.city || !addr.country) {
      return toast.warn("Fill in address (street, city, country at minimum).");
    }

    try {
      setSubmitting(true);
      // We can omit items entirely to use server-side cart fallback,
      // but let’s send for clarity and parity with mobile:
      const payload = {
        paymentMethod,
        shippingAddress: addr,
        items: items.map((ci) => ({
          productId: ci.product?._id || ci.product, // populated or raw id
          quantity: ci.quantity,
          pricePerUnit: ci.pricePerUnit,
          name: ci.product?.name, 
        })),
        buyerPhone: buyerPhone?.trim() || "",
      };

      const res = await api.orders.place(payload);
      // Clear cart client-side as well (server already clears on success)
      await api.cart.clear();

      toast.warn(`Order placed successfully${res?.orderId ? `: #${res.orderId}` : ""}`);
      navigate("/orders");
    } catch (err) {
      console.warn("Place order failed:", err?.data || err?.message || err);
      toast.warn(err?.data?.error || err?.data?.message || "Failed to place order.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#fff8e1]">
      <div className="app-container [font-family:'Nunito',Helvetica] text-[#5d4037] pb-40">
        {/* Topbar */}
        <div className="w-full flex items-center mt-4">
          <button onClick={() => navigate(-1)} className="ml-4 w-9 h-9 rounded-full bg-[#ccb357] grid place-items-center" aria-label="Back">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <h1 className="flex-1 text-center font-bold text-[18px]">Place Order</h1>
          <div className="w-9" />
        </div>

        {loading && <div className="px-4 mt-4 text-sm">Loading cart…</div>}

        {!loading && (
          <form onSubmit={place} className="px-4 mt-4">
            {/* Items (from cart) */}
            <p className="font-extrabold uppercase text-[13px] tracking-wide">Items</p>
            {items.length === 0 && <div className="mt-2 text-sm">Your cart is empty.</div>}
            <div className="mt-2 space-y-2">
              {items.map((it) => {
                const id = it.product?._id || it.product;
                const name = it.product?.name || "Item";
                const img = it.product?.images?.[0];
                const unit = Number(it.pricePerUnit || 0);
                return (
                  <div key={id} className="rounded-[12px] bg-[#eadfb7] p-3">
                    <div className="flex items-center gap-3">
                      <img
                        src={img || "https://via.placeholder.com/48x48?text=IMG"}
                        alt=""
                        className="w-12 h-12 rounded-[8px] object-cover"
                      />
                      <div className="flex-1">
                        <div className="font-bold leading-5">{name}</div>
                        <div className="text-[12px]">₦{unit.toLocaleString()} / unit</div>
                      </div>
                      <input
                        className="w-[80px] h-[36px] rounded-[10px] bg-[#fff8e1] border border-[#5d4037] px-3 outline-none"
                        type="number"
                        min="0"
                        value={it.quantity}
                        onChange={(e) => changeQty(id, e.target.value)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Address */}
            <p className="mt-5 font-extrabold uppercase text-[13px] tracking-wide">Shipping Address</p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <input className="h-[42px] rounded-[10px] bg-[#fff8e1] border border-[#5d4037] px-3"
                     placeholder="Street" value={addr.street}
                     onChange={(e) => setAddr((a) => ({ ...a, street: e.target.value }))}/>
              <input className="h-[42px] rounded-[10px] bg-[#fff8e1] border border-[#5d4037] px-3"
                     placeholder="City" value={addr.city}
                     onChange={(e) => setAddr((a) => ({ ...a, city: e.target.value }))}/>
              <input className="h-[42px] rounded-[10px] bg-[#fff8e1] border border-[#5d4037] px-3"
                     placeholder="State" value={addr.state}
                     onChange={(e) => setAddr((a) => ({ ...a, state: e.target.value }))}/>
              <input className="h-[42px] rounded-[10px] bg-[#fff8e1] border border-[#5d4037] px-3"
                     placeholder="ZIP" value={addr.zip}
                     onChange={(e) => setAddr((a) => ({ ...a, zip: e.target.value }))}/>
              <input className="col-span-2 h-[42px] rounded-[10px] bg-[#fff8e1] border border-[#5d4037] px-3"
                     placeholder="Country" value={addr.country}
                     onChange={(e) => setAddr((a) => ({ ...a, country: e.target.value }))}/>
            </div>
            
            {/* Buyer's Phone */}
            <p className="mt-5 font-extrabold uppercase text-[13px] tracking-wide">Contact</p>
              <input
                className="mt-2 w-full h-[42px] rounded-[10px] bg-[#fff8e1] border border-[#5d4037] px-3"
                placeholder="Your phone number"
                value={buyerPhone}
                onChange={(e) => setBuyerPhone(e.target.value)}
            />

            {/* Payment */}
            <p className="mt-5 font-extrabold uppercase text-[13px] tracking-wide">Payment</p>
            <div className="mt-2">
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full h-[44px] rounded-[10px] bg-[#fff8e1] border border-[#5d4037] px-3"
              >
                <option>Cash on delivery</option>
                <option>Card payment</option>
                <option>Crypto payment</option>
              </select>
            </div>

            {/* Total + actions */}
            <div className="mt-6 flex items-center justify-between">
              <div className="font-extrabold text-[18px]">Total: ₦{total.toLocaleString()}</div>
              <button
                type="submit"
                disabled={submitting || items.length === 0}
                className={`w-[160px] h-[48px] rounded-[24px] font-bold text-[#5d4037] ${
                  submitting ? "bg-[#8f814f] opacity-80 cursor-not-allowed" : "bg-[#f7c945] hover:opacity-90"
                }`}
              >
                {submitting ? "Placing..." : "Place Order"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
